/**
 * Swym "Save For Later" for the cart drawer.
 *
 * Ports the behaviour of assets/save-for-later-cart.js: move a cart line into a Swym wishlist
 * named "Save For Later", creating that list if the customer doesn't have one, then remove the
 * line from the cart.
 *
 * The legacy file does extensive manual DOM surgery afterwards — rewriting the subtotal,
 * removing the row, toggling the empty state. None of that is needed here: the store re-reads
 * the cart from the server and Vue re-renders, so this module only owns the Swym side.
 *
 * Swym API surface used (all callback-based, no promises):
 *   _swat.fetchLists({ callbackFn, errorFn })  -> [{ lid, lname, listcontents: [{ epi }] }]
 *   _swat.createList({ lname }, cb)            -> { lid }
 *   _swat.addToList(lid, product, cb)          -> product echo
 *
 * No module system in assets/ — attaches window.BrightonDrawerSwym.
 */
(function () {
  if (window.BrightonDrawerSwym) return;

  const LIST_NAME = 'save for later';

  /**
   * Swym boots asynchronously and announces itself through window.SwymCallbacks. Resolving
   * through that queue avoids polling, and works whether Swym loads before or after us.
   */
  const ready = new Promise((resolve) => {
    if (window._swat) return resolve(window._swat);
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push((swat) => resolve(swat || window._swat));
    return undefined;
  });

  const fetchLists = (swat) =>
    new Promise((resolve, reject) => {
      swat.fetchLists({
        callbackFn: resolve,
        errorFn: (xhr) => reject(new Error(`Swym fetchLists failed: ${xhr && xhr.status}`)),
      });
    });

  const findSaveForLaterList = (lists) =>
    (lists || []).find((l) => String(l.lname || '').toLowerCase() === LIST_NAME) || null;

  const createList = (swat) =>
    new Promise((resolve, reject) => {
      try {
        swat.createList({ lname: 'Save For Later' }, resolve);
      } catch (e) {
        reject(e);
      }
    });

  const addToList = (swat, lid, product) =>
    new Promise((resolve, reject) => {
      try {
        swat.addToList(lid, product, resolve);
      } catch (e) {
        reject(e);
      }
    });

  /**
   * Builds Swym's product payload from a drawer line item. Keys are Swym's own short names:
   * epi = variant id, empi = product id, du = product URL, iu = image, pr = price,
   * dt = product title, vi = variant title.
   *
   * `du` must be absolute — the legacy code prefixes shop.url; the drawer state only carries a
   * relative path, so it is resolved against the current origin.
   */
  const toSwymProduct = (item) => ({
    epi: item.variantId,
    empi: item.productId,
    du: new URL(item.url, window.location.origin).href,
    iu: item.image,
    pr: (Number(item.unitPrice || 0) / 100).toFixed(2),
    dt: item.title,
    vi: item.variantTitle,
  });

  /** Variant ids already in the Save For Later list, so saved items don't offer it again. */
  const savedVariantIds = async () => {
    const swat = await ready;
    const lists = await fetchLists(swat);
    const list = findSaveForLaterList(lists);
    if (!list || !Array.isArray(list.listcontents)) return [];
    return list.listcontents.map((c) => Number(c.epi)).filter((n) => !Number.isNaN(n));
  };

  /**
   * Saves a line to the wishlist. Returns true only if Swym accepted it, so the caller can
   * decide whether to remove the cart line — losing the item from both places would be worse
   * than leaving it in the cart.
   */
  const save = async (item) => {
    const swat = await ready;
    const lists = await fetchLists(swat);
    let list = findSaveForLaterList(lists);
    if (!list) list = await createList(swat);
    if (!list || !list.lid) throw new Error('Swym list unavailable');
    await addToList(swat, list.lid, toSwymProduct(item));
    return true;
  };

  window.BrightonDrawerSwym = { ready, savedVariantIds, save, toSwymProduct };
})();
